import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { Navigate, Outlet, useLocation, useMatch } from 'react-router-dom';
import LoadingOverlay from '../components/LoadingOverlay.jsx';
import Alert from '../components/Alert.jsx';
import Prompt from '../components/Prompt.jsx';
import ConnectivityVisual from '../components/ConnectivityVisual.jsx';
import { trackPageView } from '../util/Analytics.jsx';
import { getDatasets } from '../offline-features/getDatasets.js';

import { useStateValue } from '../context/state.jsx';
import { startup } from '../util/startup.js';
import { setIsOnline } from '../context/onlineStatus.js';
import { setDatasets } from '../context/datasets.js';
import { setRedirectURL } from '../context/redirectURL.js';

const AUTH_PAGES = new Set(['app', 'setup', 'superadmin']);

const SiteWalkerPage = ({
	requestURI,
	children
}) => {
	const location = useLocation();
	const [{ tokenExpired, datasets, emailAddress }, dispatch] = useStateValue();
	const matchDeep = useMatch('/app/dataset/:datasetId/*');
	const matchBase = useMatch('/app/dataset/:datasetId');
	const datasetId = matchDeep?.params?.datasetId ?? matchBase?.params?.datasetId ?? null;
	const hasToken = Boolean(localStorage.getItem('token'));

	const isAuthPage = pathname => {
		const page = (pathname || '').split('/').filter(Boolean)[0] || '';
		return AUTH_PAGES.has(page);
	};

	const isAuthenticatedAppRoute = pathname => {
		return isAuthPage(pathname) && hasToken;
	};

	const isLocalDevAppRoute = pathname => {
		if (!import.meta.env.DEV) {
			return false;
		}
		const page = pathname.split('/')[1];
		return page === 'app' || page === 'setup';
	};

	/** Public routes that must remain reachable after a session expires (forgot/reset password, signup, etc.). */
	const isPublicAuthPath = pathname => {
		const page = (pathname || '').split('/').filter(Boolean)[0] || '';
		return new Set([
			'',
			'signin',
			'signup',
			'signinhelp',
			'resetpassword',
			'verify',
			'invite',
			'about',
			'privacy'
		]).has(page);
	};

	useEffect(() => {
		trackPageView();

		const handleOfflineEvent = () => {
			dispatch(setIsOnline(false));
			getDatasets(dispatch).then(result => {
				if (result.success) {
					dispatch(setDatasets(result.datasets));
				}
			});
		};

		const handleOnlineEvent = () => {
			dispatch(setIsOnline(true));
			if (!isAuthenticatedAppRoute(location.pathname)) {
				return;
			}
			startup({ dispatch, datasets, datasetId, emailAddress });
		};

		window.addEventListener('offline', handleOfflineEvent);
		window.addEventListener('online', handleOnlineEvent);
		return () => {
			window.removeEventListener('offline', handleOfflineEvent);
			window.removeEventListener('online', handleOnlineEvent);
		};
	}, [location.pathname, datasets, datasetId, emailAddress]);

	useEffect(() => {
		if (isAuthenticatedAppRoute(location.pathname) || isLocalDevAppRoute(location.pathname)) {
			startup({ dispatch, datasets, datasetId, emailAddress });
		}
	}, [location.pathname, datasetId]);

	const needsSignIn = isAuthPage(location.pathname)
		&& (tokenExpired || !hasToken)
		&& !isPublicAuthPath(location.pathname)
		&& !isLocalDevAppRoute(location.pathname);

	useEffect(() => {
		if (!needsSignIn) {
			return;
		}
		// Preserve deep links (e.g. /app/dataset/:id) across the sign-in redirect.
		const returnTo = `${location.pathname}${location.search || ''}`;
		if (returnTo && returnTo !== '/app/' && returnTo !== '/app') {
			dispatch(setRedirectURL(returnTo));
		}
	}, [needsSignIn, location.pathname, location.search, dispatch]);

	if (requestURI && requestURI.pathName) {
		return <Navigate to={requestURI.pathName} replace />;
	}
	if (needsSignIn) {
		return <Navigate to="/signin/" replace />;
	}
	return (
		<div className="pageContainer">
			{children || <Outlet />}
			<Prompt />
			<Alert />
			<LoadingOverlay />
			<ConnectivityVisual />
		</div>
	);
};

SiteWalkerPage.propTypes = {
	requestURI: PropTypes.object,
	children: PropTypes.node
};

export default SiteWalkerPage;
